import React, { useState, useEffect, useRef } from 'react';
import { LineChart, ActivitySquare, MessagesSquare } from 'lucide-react';
import styles from './FeaturesSection.module.css';

const FEATURES = [
  {
    id: 1,
    icon: ActivitySquare,
    title: "Real-time Analysis",
    description: "Instant feedback for better practice",
    color: "#4a9eff"
  },
  {
    id: 2,
    icon: LineChart,
    title: "Performance Insights",
    description: "AI-powered analytics on pitch, pace, clarity",
    color: "#ff4a9e"
  },
  {
    id: 3,
    icon: MessagesSquare,
    title: "AI Practice Sessions",
    description: "Train with an AI coach for real-world scenarios",
    color: "#9eff4a"
  }
];

const FeatureAnimation = ({ feature }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const waveRef = useRef(0);
  const robotRef = useRef({ x: 0, y: 0, expression: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      switch (feature.id) {
        case 1:
          drawRealTimeAnalysis(ctx, canvas, feature.color);
          break;
        case 2:
          drawPerformanceMetrics(ctx, canvas, feature.color);
          break;
        case 3:
          drawAICoach(ctx, canvas, feature.color);
          break;
        default:
          break;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    const drawRealTimeAnalysis = (ctx, canvas, color) => {
      const time = waveRef.current;

      // Face Engagement Analysis
      ctx.save();
      ctx.translate(canvas.width * 0.2, canvas.height * 0.3);
      drawFaceEngagement(ctx, time, color);
      ctx.restore();

      // Gesture Activity
      ctx.save();
      ctx.translate(canvas.width * 0.5, canvas.height * 0.5);
      drawGestureActivity(ctx, time, color);
      ctx.restore();

      // Posture Analysis
      ctx.save();
      ctx.translate(canvas.width * 0.8, canvas.height * 0.3);
      drawPostureAnalysis(ctx, time, color);
      ctx.restore();

      // Connect elements with flowing data lines
      drawDataFlowLines(ctx, time, color);

      waveRef.current += 0.02;
    };

    const drawFaceEngagement = (ctx, time, color) => {
      const size = 60;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // Face outline
      ctx.beginPath();
      ctx.arc(0, 0, size, 0, Math.PI * 2);
      ctx.stroke();

      // Dynamic elements (eyes, mouth) that move with time
      const eyeY = Math.sin(time * 2) * 5;

      // Eyes
      ctx.beginPath();
      ctx.arc(-20, -10 + eyeY, 8, 0, Math.PI * 2);
      ctx.arc(20, -10 + eyeY, 8, 0, Math.PI * 2);
      ctx.stroke();

      // Animated mouth
      ctx.beginPath();
      ctx.moveTo(-30, 20);
      ctx.quadraticCurveTo(0, 20 + Math.sin(time * 3) * 10, 30, 20);
      ctx.stroke();

      // Engagement indicators
      const indicators = 8;
      for (let i = 0; i < indicators; i++) {
        const angle = (i / indicators) * Math.PI * 2 + time;
        const radius = size + 10 + Math.sin(time * 3 + i) * 5;

        ctx.beginPath();
        ctx.moveTo(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius
        );
        ctx.lineTo(
          Math.cos(angle) * (radius + 10),
          Math.sin(angle) * (radius + 10)
        );
        ctx.stroke();
      }
    };

    const drawGestureActivity = (ctx, time, color) => {
      const armLength = 40;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // Draw multiple arm positions to show movement
      for (let i = 0; i < 3; i++) {
        const offset = (time + i * 0.5) % (Math.PI * 2);

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(
          Math.cos(offset) * armLength,
          Math.sin(offset) * armLength
        );
        ctx.stroke();

        // Hand
        ctx.beginPath();
        ctx.arc(
          Math.cos(offset) * armLength,
          Math.sin(offset) * armLength,
          5,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      }

      // Activity zone
      ctx.beginPath();
      ctx.arc(0, 0, armLength + 10, 0, Math.PI * 2);
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Movement tracking particles
      for (let i = 0; i < 5; i++) {
        const particleOffset = (time * 2 + i) % (Math.PI * 2);
        const radius = armLength + Math.sin(time * 3 + i) * 10;

        ctx.fillStyle = `${color}${Math.floor((1 - i / 5) * 255).toString(16).padStart(2, '0')}`;
        ctx.beginPath();
        ctx.arc(
          Math.cos(particleOffset) * radius,
          Math.sin(particleOffset) * radius,
          3,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
    };

    const drawPostureAnalysis = (ctx, time, color) => {
      const height = 80;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // Spine line with posture curve
      ctx.beginPath();
      ctx.moveTo(0, -height / 2);
      ctx.quadraticCurveTo(
        Math.sin(time) * 10,
        0,
        0,
        height / 2
      );
      ctx.stroke();

      // Shoulder line
      ctx.beginPath();
      ctx.moveTo(-30, -height / 2 + 10);
      ctx.lineTo(30, -height / 2 + 10);
      ctx.stroke();

      // Analysis points
      const points = 5;
      for (let i = 0; i < points; i++) {
        const y = -height / 2 + (height * i / (points - 1));
        const x = Math.sin(time + i) * 5;

        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.stroke();

        // Analysis lines
        ctx.beginPath();
        ctx.moveTo(x + 10, y);
        ctx.lineTo(x + 30, y);
        ctx.stroke();

        // Analysis indicators
        const indicatorWidth = 15 + Math.sin(time * 2 + i) * 5;
        ctx.fillStyle = `${color}33`;
        ctx.fillRect(x + 30, y - 2, indicatorWidth, 4);
      }
    };

    const drawDataFlowLines = (ctx, time, color) => {
      ctx.strokeStyle = `${color}44`;
      ctx.lineWidth = 1;

      // Draw flowing data lines between elements
      const points = [
        [canvas.width * 0.2, canvas.height * 0.3],
        [canvas.width * 0.5, canvas.height * 0.5],
        [canvas.width * 0.8, canvas.height * 0.3]
      ];

      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const [x1, y1] = points[i];
          const [x2, y2] = points[j];

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);

          // Animate flowing dots along the lines
          const particleCount = 3;
          for (let k = 0; k < particleCount; k++) {
            const progress = (time + k / particleCount) % 1;
            const x = x1 + (x2 - x1) * progress;
            const y = y1 + (y2 - y1) * progress;

            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(x, y, 2, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.stroke();
        }
      }
    };

    const drawPerformanceMetrics = (ctx, canvas, color) => {
      const time = waveRef.current;  

      const metrics = [
        { label: 'Clarity', value: Math.sin(time * 0.5) * 0.2 + 0.7 },
        { label: 'Pitch', value: Math.sin(time * 0.7 + 1) * 0.2 + 0.8 },
        { label: 'Pause', value: Math.sin(time * 0.3 + 2) * 0.15 + 0.75 },
        { label: 'Improvement', value: Math.sin(time * 0.4 + 3) * 0.1 + 0.85 }
      ];

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY) * 0.7;

      // Draw connecting lines
      ctx.strokeStyle = `${color}33`;
      ctx.beginPath();
      metrics.forEach((_, i) => {
        const angle = (i / metrics.length) * Math.PI * 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();
      ctx.stroke();

      // Draw metrics
      metrics.forEach((metric, i) => {
        const angle = (i / metrics.length) * Math.PI * 2;
        const distance = radius * metric.value;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;

        // Draw point
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();

        // Draw label
        const labelX = centerX + Math.cos(angle) * (radius + 20);
        const labelY = centerY + Math.sin(angle) * (radius + 20);

        ctx.fillStyle = '#ffffff';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(metric.label, labelX, labelY);

        // Draw value line
        ctx.strokeStyle = `${color}88`;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.stroke();
      });

      // Draw animated center
      ctx.fillStyle = `${color}22`;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 10 + Math.sin(time * 2) * 5, 0, Math.PI * 2);
      ctx.fill();

      waveRef.current += 0.02;
    };

    const drawAICoach = (ctx, canvas, color) => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Update robot position
      robotRef.current.x = centerX + Math.sin(waveRef.current * 0.5) * 20;
      robotRef.current.y = centerY + Math.cos(waveRef.current * 0.7) * 10;
      robotRef.current.expression = Math.sin(waveRef.current * 2);

      // Draw robot body
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // Head
      ctx.beginPath();
      ctx.roundRect(
        robotRef.current.x - 30,
        robotRef.current.y - 40,
        60,
        60,
        10
      );
      ctx.stroke();

      // Eyes
      const eyeY = robotRef.current.y - 20 + Math.sin(waveRef.current * 3) * 2;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(robotRef.current.x - 15, eyeY, 5, 0, Math.PI * 2);
      ctx.arc(robotRef.current.x + 15, eyeY, 5, 0, Math.PI * 2);
      ctx.fill();

      // Animated mouth
      ctx.beginPath();
      ctx.moveTo(robotRef.current.x - 20, robotRef.current.y);
      ctx.quadraticCurveTo(
        robotRef.current.x,
        robotRef.current.y + robotRef.current.expression * 10,
        robotRef.current.x + 20,
        robotRef.current.y
      );
      ctx.stroke();

      // Antenna
      ctx.beginPath();
      ctx.moveTo(robotRef.current.x, robotRef.current.y - 40);
      ctx.lineTo(robotRef.current.x, robotRef.current.y - 55);
      ctx.stroke();

      // Signal waves
      const waves = 3;
      for (let i = 0; i < waves; i++) {
        const waveOffset = (waveRef.current + i / waves) % 1;
        const waveSize = 20 + waveOffset * 30;

        ctx.beginPath();
        ctx.arc(
          robotRef.current.x,
          robotRef.current.y - 55,
          waveSize,
          -Math.PI * 0.6,
          -Math.PI * 0.4
        );
        ctx.strokeStyle = `${color}${Math.floor((1 - waveOffset) * 255).toString(16).padStart(2, '0')}`;
        ctx.stroke();
      }

      // Floating text bubbles
      const messages = [
        "How can I help?",
        "Let's practice!",
        "Great progress!",
        "Try again!"
      ];

      messages.forEach((msg, i) => {
        const bubbleOffset = ((waveRef.current * 0.5) + i / messages.length) * Math.PI * 2;
        const bubbleX = robotRef.current.x + Math.cos(bubbleOffset) * 100;
        const bubbleY = robotRef.current.y + Math.sin(bubbleOffset) * 60;

        // Draw bubble
        ctx.fillStyle = `${color}11`;
        ctx.strokeStyle = `${color}88`;
        ctx.beginPath();
        const bubbleWidth = ctx.measureText(msg).width + 20;
        const bubbleHeight = 30;
        ctx.roundRect(
          bubbleX - bubbleWidth / 2,
          bubbleY - bubbleHeight / 2,
          bubbleWidth,
          bubbleHeight,
          10
        );
        ctx.fill();
        ctx.stroke();

        // Draw text
        ctx.fillStyle = '#ffffff';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(msg, bubbleX, bubbleY);

        // Connect bubble to robot
        ctx.strokeStyle = `${color}44`;
        ctx.beginPath();
        ctx.moveTo(robotRef.current.x, robotRef.current.y - 20);
        ctx.quadraticCurveTo(
          (robotRef.current.x + bubbleX) / 2,
          (robotRef.current.y + bubbleY) / 2 - 20,
          bubbleX,
          bubbleY
        );
        ctx.stroke();
      });

      // Interactive particles
      if (!particlesRef.current.length) {
        particlesRef.current = Array.from({ length: 30 }, () => ({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 1,
          speedX: (Math.random() - 0.5) * 2,
          speedY: (Math.random() - 0.5) * 2,
          opacity: Math.random()
        }));
      }

      // Update and draw particles
      particlesRef.current.forEach(particle => {
        // Move towards robot
        const dx = robotRef.current.x - particle.x;
        const dy = robotRef.current.y - particle.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 150) {
          particle.speedX += dx / dist * 0.1;
          particle.speedY += dy / dist * 0.1;
        } else {
          particle.speedX *= 0.95;
          particle.speedY *= 0.95;
        }

        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Wrap around screen
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Draw particle
        ctx.fillStyle = `${color}${Math.floor(particle.opacity * 255).toString(16).padStart(2, '0')}`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });

      waveRef.current += 0.02;
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [feature]);

  return <canvas ref={canvasRef} className={styles.featureCanvas} />;
};

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(FEATURES[0]);

  return (
    <section id='features' className={styles.featuresSection}>
      <div className={styles.header}>
        <h2>Perfect Your Speaking Skills</h2>
        <p>Advanced features designed for comprehensive speaking improvement</p>
      </div>

      <div className={styles.tvContainer}>
        <div className={styles.tvFrame}>
          <div className={styles.screen}>
            <FeatureAnimation feature={activeFeature} />
            <div className={styles.glowOverlay} />
            <div className={styles.scanline} />
          </div>
        </div>
      </div>

      <div className={styles.featuresGrid}>
        {FEATURES.map(feature => (
          <button
            key={feature.id}
            className={`${styles.featureButton} ${activeFeature.id === feature.id ? styles.active : ''}`}
            onClick={() => setActiveFeature(feature)}
            style={{ '--feature-color': feature.color }}
          >
            <feature.icon className={styles.featureIcon} />
            <div className={styles.featureContent}>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;